/**
 * QuickComposeModal
 * 
 * Lightweight modal for quickly sending a DM from the dashboard.
 * Features: contact multi-select, rich text (tiptap), image attachments, GIF picker.
 * Reusable across widgets via props (preSelectedUsers, title, etc.)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2, Send, Smile, Bold, Italic, Underline as UnderlineIcon, User, Check, AlertCircle, UserPlus, FileText, Video } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserAvatar } from '@/components/shared';
import { GifPickerDialog } from '@/components/comms/components/GifPicker';
import { createDirectConversation, createGroupConversation, sendTextMessage, sendImageMessage, sendVideoMessage } from '@/lib/firestore';
import { useContactSelection } from '@/components/messaging/hooks/useContactSelection';
import { UpgradeRequiredDialog } from '@/components/shared/UpgradeRequiredDialog';
import { uploadWithProgress, uploadVideoWithProgress } from '@/lib/uploadVideoWithProgress';
import AttachmentMenu from '@/components/shared/AttachmentMenu';
import { toast } from 'sonner';
import { IMAGE_ACCEPT, isImageFile } from '@/lib/imageFiles';
import LocalImageThumb from '@/components/shared/LocalImageThumb';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 10;

export default function QuickComposeModal({
  open,
  onClose,
  user,
  title = 'Quick Message',
  preSelectedUsers = [],
  lockRecipients = false,
  pcoTeamMembers = null,
  // Services (Phase 5): {members:[{user_id,user_name,user_email}]} from
  // POST /api/services/quick-message-roster — WOS user ids, no PCO matching.
  directRoster = null,
  groupName = null,
  planUrl = null,
  // Services (Fix 6C-2, v2.9): plain-text service details from quick-message-roster.
  initialBody = null,
  rightSidebar = null,
}) {
  const { isStudio } = useDesign();
  // ── PCO team matching state ──
  const [pcoMatchedPreSelect, setPcoMatchedPreSelect] = useState([]);
  const [unmatchedMembers, setUnmatchedMembers] = useState([]);
  
  // ── PCO invite state ──
  const [invitingUnmatched, setInvitingUnmatched] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  // When opened from PC widget, use match results as pre-selection; otherwise use prop
  const effectivePreSelected = pcoTeamMembers?.length > 0 ? pcoMatchedPreSelect : preSelectedUsers;

  const {
    selectedUsers,
    searchQuery,
    setSearchQuery,
    searchInputRef,
    loading,
    error,
    filteredMembers,
    toggleUser,
    isSelected,
  } = useContactSelection({ open, user, preSelectedUsers: effectivePreSelected });

  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedGif, setSelectedGif] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);                 // [{ url, filename, size, type }]
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileAttachRef = useRef(null);

  const [, setEditorState] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, bulletList: false, orderedList: false,
        blockquote: false, codeBlock: false, code: false,
        horizontalRule: false, hardBreak: true,
      }),
      Underline.configure({ HTMLAttributes: {} }),
      // QuickCompose: make Enter insert a line break (identical to Shift+Enter)
      // so what's typed matches exactly what's sent. Cmd/Ctrl+Enter still sends
      // (handled in editorProps.handleKeyDown below); the Send button is unaffected.
      Extension.create({
        name: 'enterAsLineBreak',
        addKeyboardShortcuts() {
          return {
            Enter: ({ editor: ed }) => ed.commands.setHardBreak(),
          };
        },
      }),
    ],
    content: '',
    onTransaction: () => setEditorState(n => n + 1),
    editorProps: {
      handleKeyDown: (view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault();
          document.querySelector('[data-testid="quick-compose-send-btn"]')?.click();
          return true;
        }
        return false;
      },
      attributes: {
        class: 'outline-none h-full text-sm text-slate-800 dark:text-slate-100 leading-relaxed',
        style: 'white-space:pre-wrap;word-break:break-word;',
        'data-placeholder': 'Type your message...',
      },
    },
  });

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSelectedImages(prev => {
        prev.forEach(img => URL.revokeObjectURL(img.preview));
        return [];
      });
      setSelectedGif(null);
      setSelectedVideo(null);
      setVideoUploadProgress(null);
      setSelectedFiles([]);
      setIsUploadingFiles(false);
      setFileUploadProgress(null);
      setSending(false);
      setSent(false);
      setPcoMatchedPreSelect([]);
      setUnmatchedMembers([]);
      if (editor) editor.commands.clearContent();
    }
  }, [open, editor]);

  // ── Pre-fill plan link when opened from PCO widget ──
  useEffect(() => {
    if (open && planUrl && editor) {
      editor.commands.setContent(`<p>🔗 Link to Plan: <a href="${planUrl}">${planUrl}</a></p><p></p><p></p>`);
      editor.commands.focus('end');
    }
  }, [open, planUrl, editor]);

  // ── Services (Fix 6C-2, v2.9): pre-fill the service details; never over typed text ──
  useEffect(() => {
    if (open && initialBody && editor && editor.isEmpty) {
      const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      editor.commands.setContent(initialBody.split('\n').map(line => `<p>${esc(line)}</p>`).join('') + '<p></p>');
      editor.commands.focus('end');
    }
  }, [open, initialBody, editor]);

  // ── PCO team matching: call backend to match person_ids → WOS users ──
  useEffect(() => {
    if (!open || !pcoTeamMembers?.length || !user?.church_id) return;
    if (directRoster) return; // Services roster: already WOS user ids, skip match-team

    let cancelled = false;

    const matchTeam = async () => {
      // Filter out declined members — they shouldn't be in the message thread
      const activeMembers = pcoTeamMembers.filter(m => m.status !== 'declined');

      const personIds = activeMembers
        .map(m => m.person_id)
        .filter(pid => pid && !pid.startsWith('_no_id_'));

      if (personIds.length === 0) {
        setUnmatchedMembers(activeMembers.map(m => ({ name: m.name, person_id: m.person_id || null })));
        return;
      }

      try {
        const resp = await fetch(`${API_URL}/api/planning-center/match-team`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ person_ids: personIds, church_id: user.church_id, user_id: user.id }),
        });
        if (cancelled) return;

        const data = await resp.json();

        if (data.matched_user_ids?.length > 0) {
          setPcoMatchedPreSelect(data.matched_user_ids.map(id => ({ id })));
        }

        // Compute unmatched: backend-reported unmatched + members with no person_id.
        // People the admin explicitly dismissed ("Not in Worship OS") are neither
        // pre-selected nor listed as unmatched (P2-j, DIV-040).
        const unmatchedSet = new Set(data.unmatched_person_ids || []);
        const dismissedSet = new Set(data.dismissed_person_ids || []);
        const allUnmatched = activeMembers
          .filter(m => !dismissedSet.has(m.person_id))
          .filter(m => !m.person_id || unmatchedSet.has(m.person_id))
          .map(m => ({ name: m.name, person_id: m.person_id || null }));
        if (allUnmatched.length > 0) setUnmatchedMembers(allUnmatched);
      } catch (err) {
        console.error('Error matching PCO team:', err);
      }
    };

    matchTeam();
    return () => { cancelled = true; };
  }, [open, pcoTeamMembers, user?.church_id]);

  // ── Services roster: the direct list is the matched pre-selection ──
  useEffect(() => {
    if (!open || !directRoster) return;
    setPcoMatchedPreSelect((directRoster.members || []).map(m => ({ id: m.user_id })));
    setUnmatchedMembers([]);
  }, [open, directRoster]);

  // ── Media handlers ──────────────────────────────────────────────

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    const imageFiles = files.filter(isImageFile);
    if (imageFiles.length === 0) return;
    if (imageFiles.some(f => f.size > 7 * 1024 * 1024)) { // R15: server cap is 7 MB
      toast.error('Image file size must be under 7MB.');
      return;
    }
    const toAdd = imageFiles.slice(0, MAX_IMAGES - selectedImages.length);
    const newImages = toAdd.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setSelectedImages(prev => [...prev, ...newImages]);
    if (selectedGif) setSelectedGif(null);
    if (selectedVideo) setSelectedVideo(null);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  const handleGifSelect = (gif) => {
    setSelectedGif(gif);
    setShowGifPicker(false);
    if (selectedImages.length > 0) {
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
      setSelectedImages([]);
    }
    if (selectedVideo) setSelectedVideo(null);
  };

  const handleFileAttachSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (selectedFiles.length + files.length > 5) {
      toast.error('Maximum 5 files allowed.');
      return;
    }
    if (files.some(f => f.size > 60 * 1024 * 1024)) { // R15: server cap is 60 MB
      toast.error('File size must be under 60MB.');
      return;
    }
    setIsUploadingFiles(true);
    setFileUploadProgress(0);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const data = await uploadWithProgress(
        `${API_URL}/api/comms/v3/upload/files?user_id=${encodeURIComponent(user.id)}`,
        formData,
        (pct) => setFileUploadProgress(pct),
        0
      );
      setSelectedFiles(prev => [...prev, ...data.files]);
    } catch (err) {
      console.error('Error uploading files:', err);
      toast.error(err.message || 'File upload failed.');
    } finally {
      setIsUploadingFiles(false);
      setFileUploadProgress(null);
      if (fileAttachRef.current) fileAttachRef.current.value = '';
    }
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (file.size > 400 * 1024 * 1024) { toast.error('Video must be under 400MB.'); return; }
    try {
      const duration = await new Promise((resolve, reject) => {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => { URL.revokeObjectURL(vid.src); resolve(vid.duration); };
        vid.onerror = () => { URL.revokeObjectURL(vid.src); reject(); };
        vid.src = URL.createObjectURL(file);
      });
      if (duration > 240) { toast.error('Video must be under 4 minutes.'); return; }
    } catch { /* server enforces */ }

    setVideoUploadProgress(0);
    try {
      const data = await uploadVideoWithProgress(
        `${API_URL}/api/comms/v3/messaging/upload?user_id=${encodeURIComponent(user.id)}&media_type=video`,
        file,
        (pct) => setVideoUploadProgress(pct)
      );
      const vid = data.media[0];
      setSelectedVideo({ url: vid.url, thumbnailUrl: vid.thumbnail_url, duration: vid.duration, width: vid.width, height: vid.height });
      if (selectedImages.length > 0) { selectedImages.forEach(img => URL.revokeObjectURL(img.preview)); setSelectedImages([]); }
      if (selectedGif) setSelectedGif(null);
      toast.success('Video uploaded!');
    } catch (err) {
      toast.error(err.message || 'Video upload failed.');
    } finally {
      setVideoUploadProgress(null);
    }
  };

  const uploadImages = async (images) => {
    const formData = new FormData();
    images.forEach(img => formData.append('files', img.file));
    const result = await uploadWithProgress(
      `${API_URL}/api/comms/v3/messaging/upload?user_id=${encodeURIComponent(user.id)}&media_type=image`,
      formData,
      () => {}, 0
    );
    return result.media.map(item => ({ url: item.url, width: 0, height: 0 }));
  };

  // ── Send ────────────────────────────────────────────────────────

  const stripHtml = (html) => html ? html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() : '';

  const handleSend = useCallback(async () => {
    if (selectedUsers.length === 0) { toast.error('Please select at least one recipient.'); return; }

    const html = editor?.getHTML() || '';
    const hasText = stripHtml(html).length > 0;
    const hasImages = selectedImages.length > 0;
    const hasGif = !!selectedGif;
    const hasVideo = !!selectedVideo;
    const hasFiles = selectedFiles.length > 0;

    if (!hasText && !hasImages && !hasGif && !hasVideo && !hasFiles) { toast.error('Please type a message or attach something.'); return; }

    setSending(true);
    try {
      const me = {
        id: user.id,
        name: user.name || user.user_metadata?.name || user.email,
        email: user.email,
        avatar: user.avatar || null,
      };

      const conversation = selectedUsers.length === 1
        ? await createDirectConversation(me, selectedUsers[0])
        : await createGroupConversation(me, selectedUsers, groupName);

      // Build text body with file attachments appended as HTML markup
      // (matches MessageComposer's pattern — see /lib/firestore/messages.js
      // for the receiving side, and MessageBubble for the rendering side).
      let finalText = html;
      if (hasFiles) {
        const fileHtml = selectedFiles.map(f =>
          `<p data-file-attachment="true" data-file-url="${f.url}" data-file-name="${f.filename}" data-file-size="${f.size}" data-file-type="${f.type}"><a href="${f.url}" target="_blank" rel="noopener noreferrer">${f.filename}</a></p>`
        ).join('');
        finalText = (hasText ? finalText : '') + fileHtml;
      }
      const captionText = (hasText || hasFiles) ? finalText : '';

      if (hasVideo) {
        await sendVideoMessage(conversation.id, me, selectedVideo, captionText);
      } else if (hasGif) {
        await sendImageMessage(conversation.id, me, [{ url: selectedGif.url, width: 0, height: 0 }], captionText);
      } else if (hasImages) {
        const uploaded = await uploadImages(selectedImages);
        await sendImageMessage(conversation.id, me, uploaded, captionText);
      } else {
        // Text-only OR files-only (or text + files) — both route through sendTextMessage
        await sendTextMessage(conversation.id, me, finalText);
      }

      setSent(true);
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
      setSelectedVideo(null);
      setSelectedFiles([]);
      toast.success(`Message sent to ${selectedUsers.map(u => (u.name || u.email).split(' ')[0]).join(', ')}!`);
      setTimeout(() => onClose(), 800);
    } catch (err) {
      console.error('QuickCompose send error:', err);
      toast.error('Failed to send message. Please try again.');
      setSending(false);
    }
  }, [selectedUsers, editor, selectedImages, selectedGif, selectedVideo, selectedFiles, user, onClose, groupName]);

  // ── PCO invite handlers ──────────────────────────────────────────
  const invitableMembers = unmatchedMembers.filter(m => m.person_id);
  const canInvite = user?.role === 'owner' || user?.role === 'admin';

  const handleInviteUnmatched = useCallback(async () => {
    if (invitableMembers.length === 0) return;
    setInvitingUnmatched(true);
    try {
      const resp = await fetch(`${API_URL}/api/planning-center/invite-unmatched`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_ids: invitableMembers.map(m => m.person_id),
          church_id: user.church_id,
          inviter_id: user.id,
        }),
      });
      const data = await resp.json();

      if (!data.success && data.reason === 'limit_reached') {
        if (data.upgrade_info) {
          setUpgradeInfo(data.upgrade_info);
          setShowUpgradeDialog(true);
        } else {
          // Already on highest tier — no upgrade path available
          toast.error(`You need ${data.needed_slots} slots but only have ${data.available_slots} available. Cancel pending invites to free up slots, or contact support.`);
        }
        return;
      }

      if (data.success) {
        const msgs = [];
        if (data.invited > 0) msgs.push(`Invited ${data.invited} team member${data.invited > 1 ? 's' : ''} to Worship OS`);
        if (data.skipped_no_email?.length > 0) msgs.push(`${data.skipped_no_email.length} couldn't be invited (no email in Planning Center)`);
        if (data.skipped_already?.length > 0) msgs.push(`${data.skipped_already.length} already invited or in the system`);
        toast.success(msgs.join('. '));

        // Update banner: keep only those who couldn't be invited
        const invitedSet = new Set((data.invited_names || []).map(n => n.toLowerCase()));
        const skippedAlreadySet = new Set((data.skipped_already || []).map(s => s.name.toLowerCase()));
        setUnmatchedMembers(prev => prev.filter(m => {
          const nameLower = m.name.toLowerCase();
          return !invitedSet.has(nameLower) && !skippedAlreadySet.has(nameLower);
        }));
      }
    } catch (err) {
      console.error('Error inviting PCO members:', err);
      toast.error('Failed to send invites. Please try again.');
    } finally {
      setInvitingUnmatched(false);
    }
  }, [invitableMembers, user?.church_id, user?.id]);

  const handleUpgradeAndInvite = useCallback(async () => {
    setUpgrading(true);
    try {
      const resp = await fetch(`${API_URL}/api/stripe/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          church_id: user.church_id,
          new_tier: upgradeInfo.next_tier,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        toast.success(`Upgraded to ${upgradeInfo.next_tier_name}!`);
        setShowUpgradeDialog(false);
        // Now retry the invite
        await handleInviteUnmatched();
      } else {
        toast.error(data.detail || 'Upgrade failed. Please try again.');
      }
    } catch (err) {
      console.error('Error upgrading:', err);
      toast.error('Upgrade failed. Please try again.');
    } finally {
      setUpgrading(false);
    }
  }, [user?.church_id, upgradeInfo, handleInviteUnmatched]);

  const handleDeclineUpgrade = useCallback(() => {
    setShowUpgradeDialog(false);
  }, []);

  if (!open) return null;

  const hasContent = stripHtml(editor?.getHTML() || '').length > 0 || selectedImages.length > 0 || !!selectedGif || !!selectedVideo || selectedFiles.length > 0;

  // ── Render ──────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[6px] animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="quick-compose-backdrop"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none gap-3" data-testid="quick-compose-modal">
        <div
          data-ui="float" className="pointer-events-auto w-full max-w-lg studio-w-560 bg-card rounded-2xl shadow-2xl shadow-black/20 border border-slate-200/80 animate-in zoom-in-95 fade-in duration-200 flex flex-col overflow-hidden overscroll-contain"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Send className="studio-icon-fg w-4 h-4 text-blue-500 dark:text-blue-400" />
              {isStudio && title === 'Quick Message' ? 'Quick message' : title}
            </h2>
            <button
              onClick={onClose}
              className="studio-ibtn h-8 w-8 rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 transition-all"
              data-testid="quick-compose-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* ── Success state ── */}
          {sent ? (
            <div className="flex flex-col items-center justify-center py-12 px-5 animate-in zoom-in-95 fade-in duration-300">
              <div data-ui="ok-glyph" className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center mb-3">
                <Check className="w-7 h-7 text-green-600 dark:text-green-400" />
              </div>
              <p data-ui="ok-title" className="text-base font-medium text-slate-700 dark:text-slate-200">{isStudio ? 'Message sent' : 'Message Sent!'}</p>
            </div>
          ) : (
            <>
              {/* ── Unmatched PCO team members banner ── */}
              {unmatchedMembers.length > 0 && (
                <div className="px-5 pb-2 flex-shrink-0" data-testid="pco-unmatched-banner">
                  <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-100 rounded-lg px-3 py-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          Not in Worship OS yet: {unmatchedMembers.map(m => m.name).join(', ')}
                        </p>
                      </div>
                    </div>
                    {invitableMembers.length > 0 && canInvite && (
                      <div className="flex justify-end mt-1">
                        <button
                          onClick={handleInviteUnmatched}
                          disabled={invitingUnmatched}
                          className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100 underline underline-offset-2 decoration-amber-300 hover:decoration-amber-500 transition-colors disabled:opacity-50"
                          data-testid="pco-invite-unmatched-btn"
                        >
                          {invitingUnmatched ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Inviting...</>
                          ) : (
                            <><UserPlus className="w-3 h-3" /> Click to invite them</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Recipients ── */}
              {!lockRecipients && (
                <div className="px-5 pb-3 border-b border-slate-100 dark:border-white/10 flex-shrink-0">
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2.5">
                      {selectedUsers.map(m => (
                        <Badge
                          key={m.id}
                          variant="secondary"
                          data-ui="rcp-chip"
                          className="flex items-center gap-1 pl-1 pr-2 py-1 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900 hover:bg-blue-100 transition-colors"
                        >
                          <UserAvatar userId={m.id} name={m.name || m.email} avatarUrl={m.avatar} size="xs" />
                          <span className="ml-0.5 text-xs font-medium">{(m.name || m.email).split(' ')[0]}</span>
                          <button onClick={() => toggleUser(m)} className="ml-0.5 hover:text-red-500 transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                    <Input
                      ref={searchInputRef}
                      type="text"
                      placeholder={isStudio ? 'Search team members' : 'Search team members...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 text-sm border-slate-200 dark:border-white/10 focus:border-blue-300"
                      data-testid="quick-compose-search"
                      autoFocus
                    />
                  </div>

                  <ScrollArea className="h-[160px] mt-2">
                    {loading ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400 dark:text-slate-500" />
                      </div>
                    ) : error ? (
                      <p className="text-sm text-red-500 dark:text-red-400 py-3 text-center">{error}</p>
                    ) : filteredMembers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
                        <User className="w-6 h-6 mb-1 opacity-50" />
                        <p className="text-xs">{searchQuery ? 'No members found' : 'No team members yet'}</p>
                      </div>
                    ) : (
                      <div className="space-y-0.5 pr-2">
                        {filteredMembers.map(member => (
                          <button
                            key={member.id}
                            onClick={() => toggleUser(member)}
                            data-ui="member-row"
                            data-on={isSelected(member.id) ? 'true' : undefined}
                            className={`w-full flex items-center gap-2.5 p-2 rounded-lg transition-all text-left ${
                              isSelected(member.id)
                                ? 'bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900'
                                : 'hover:bg-slate-50 dark:hover:bg-white/5 border border-transparent'
                            }`}
                            data-testid={`quick-compose-member-${member.id}`}
                          >
                            <UserAvatar userId={member.id} name={member.name || member.email} avatarUrl={member.avatar} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{member.name || member.email}</div>
                            </div>
                            {isSelected(member.id) && (
                              <div data-ui="check-round" className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              )}

              {lockRecipients && selectedUsers.length > 0 && (
                <div className="px-5 pb-3 border-b border-slate-100 dark:border-white/10 flex-shrink-0">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">To:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedUsers.map(m => (
                      <Badge
                        key={m.id}
                        variant="secondary"
                        data-ui="rcp-chip"
                        data-locked="true"
                        className="flex items-center gap-1 pl-1 pr-2 py-1 bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300"
                      >
                        <UserAvatar userId={m.id} name={m.name || m.email} avatarUrl={m.avatar} size="xs" />
                        <span className="ml-0.5 text-xs font-medium">{(m.name || m.email).split(' ')[0]}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Compose area ── */}
              <div className="px-5 pt-3 pb-3 flex flex-col flex-shrink-0">

                {/* Formatting toolbar */}
                {editor && (
                  <div data-ui="cmp-tools" className="flex items-center gap-0.5 mb-1.5">
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                      className={`p-1 rounded transition-colors ${
                        editor.isActive('bold') ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10'
                      }`}
                    >
                      <Bold className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                      className={`p-1 rounded transition-colors ${
                        editor.isActive('italic') ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10'
                      }`}
                    >
                      <Italic className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
                      className={`p-1 rounded transition-colors ${
                        editor.isActive('underline') ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10'
                      }`}
                    >
                      <UnderlineIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* 
                  Compose box — single fixed-height container.
                  Media previews + text editor live inside, scroll together.
                  Nothing outside this box ever moves.
                */}
                <div data-ui="qc-box" className="bg-slate-50 dark:bg-[#1e1f22] rounded-xl border border-slate-200 dark:border-white/10 transition-all focus-within:bg-white dark:focus-within:bg-[#2d2f33] focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-200 h-[120px] overflow-y-auto px-4 py-3 flex flex-col">

                  {/* Inline GIF preview */}
                  {selectedGif && (
                    <div className="mb-2 flex-shrink-0 animate-in fade-in duration-150">
                      <div className="relative inline-block">
                        <img
                          src={selectedGif.preview || selectedGif.url}
                          alt={selectedGif.title || 'GIF'}
                          className="h-16 rounded-lg object-cover border border-slate-200 dark:border-white/10"
                        />
                        <button
                          onClick={() => setSelectedGif(null)}
                          className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Inline image previews */}
                  {selectedImages.length > 0 && (
                    <div className="mb-2 flex gap-2 flex-wrap flex-shrink-0 animate-in fade-in duration-150">
                      {selectedImages.map((img, i) => (
                        <div key={img.preview} className="relative">
                          <LocalImageThumb
                            src={img.preview}
                            name={img.file?.name}
                            className="h-14 w-14 rounded-lg object-cover border border-slate-200 dark:border-white/10"
                          />
                          <button
                            onClick={() => handleRemoveImage(i)}
                            className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inline video preview */}
                  {selectedVideo && (
                    <div className="mb-2 flex-shrink-0 animate-in fade-in duration-150 relative inline-block">
                      <div className="h-14 w-14 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 bg-black relative">
                        {selectedVideo.thumbnailUrl ? (
                          <img src={selectedVideo.thumbnailUrl} alt="Video" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center bg-slate-800">
                            <Video className="w-4 h-4 text-white/50" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-6 h-6 rounded-full bg-black/50 flex items-center justify-center">
                            <span className="w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[7px] border-l-white ml-0.5" />
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedVideo(null)}
                        className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {/* Inline file attachments preview */}
                  {selectedFiles.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2 animate-in fade-in duration-150">
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={`${file.url}-${idx}`}
                          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 max-w-[200px] relative group"
                          data-testid={`quick-compose-file-chip-${idx}`}
                        >
                          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{file.filename}</div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">{Math.round(file.size / 1024)} KB</div>
                          </div>
                          <button
                            onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                            className="text-slate-400 dark:text-slate-500 hover:text-red-500 transition-colors"
                            aria-label="Remove file"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Text editor — fills all remaining space */}
                  <div
                    className="flex-1 cursor-text"
                    onClick={() => editor?.commands.focus()}
                  >
                    <EditorContent
                      editor={editor}
                      className="h-full [&_.tiptap]:outline-none [&_.tiptap]:h-full [&_.tiptap_p]:m-0 [&_.tiptap.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap.is-editor-empty:first-child::before]:text-slate-400 [&_.tiptap.is-editor-empty:first-child::before]:float-left [&_.tiptap.is-editor-empty:first-child::before]:h-0 [&_.tiptap.is-editor-empty:first-child::before]:pointer-events-none"
                      data-testid="quick-compose-editor"
                    />
                  </div>
                </div>

                {/* Action bar — pinned, never moves */}
                <div data-ui="qc-actions" className="flex items-center justify-between mt-3">
                  <div data-ui="cmp-tools" className="flex items-center gap-1">
                    <AttachmentMenu
                      testIdPrefix="quick-compose"
                      size="sm"
                      align="start"
                      preserveFocus
                      onClose={() => { if (editor) editor.commands.focus(); }}
                      triggerClassName="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-500/15"
                      onImageClick={() => fileInputRef.current?.click()}
                      onVideoClick={() => videoInputRef.current?.click()}
                      onFileClick={() => fileAttachRef.current?.click()}
                      imageDisabled={sending || selectedImages.length >= MAX_IMAGES || videoUploadProgress !== null}
                      videoDisabled={sending || videoUploadProgress !== null || !!selectedVideo}
                      fileDisabled={sending || isUploadingFiles || selectedFiles.length >= 5}
                      uploadProgress={
                        videoUploadProgress !== null ? videoUploadProgress
                        : fileUploadProgress !== null ? fileUploadProgress
                        : null
                      }
                      activeUploadLabel={
                        videoUploadProgress !== null ? 'video'
                        : fileUploadProgress !== null ? 'file'
                        : ''
                      }
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT}
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                      onChange={handleVideoSelect}
                      className="hidden"
                    />
                    <input
                      ref={fileAttachRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp3,.m4a,.wav,.ogg,.aac,.flac"
                      multiple
                      onChange={handleFileAttachSelect}
                      className="hidden"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-500/15 transition-all"
                      disabled={sending}
                      onClick={() => setShowGifPicker(true)}
                      title="Choose a GIF"
                      data-testid="quick-compose-gif-btn"
                    >
                      <Smile className="w-4 h-4" />
                    </Button>
                  </div>

                  <Button
                    onClick={handleSend}
                    disabled={sending || selectedUsers.length === 0 || !hasContent}
                    className="studio-primary-btn bg-blue-500 hover:bg-blue-600 text-white rounded-full px-5 h-9 text-sm font-medium shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-40 disabled:shadow-none"
                    data-testid="quick-compose-send-btn"
                  >
                    {sending ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="w-3.5 h-3.5 mr-1.5" />Send</>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        {rightSidebar && (
          <div
            className="pointer-events-auto hidden lg:block animate-in slide-in-from-right-4 fade-in duration-300 delay-100 fill-mode-backwards"
            onClick={(e) => e.stopPropagation()}
          >
            {rightSidebar}
          </div>
        )}
      </div>

      <GifPickerDialog
        open={showGifPicker}
        onOpenChange={setShowGifPicker}
        onSelect={handleGifSelect}
      />

      <UpgradeRequiredDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        upgradeInfo={upgradeInfo}
        upgrading={upgrading}
        onUpgrade={handleUpgradeAndInvite}
        onDecline={handleDeclineUpgrade}
      />
    </>
  );
}
